import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import {
    Bell, X, Check, CheckCheck, Trash2, Settings, Filter,
    FileUp, Sparkles, AlertTriangle, FileCheck, Eye,
    Ship, CheckCircle, AlertCircle, Link, Package, MapPin,
    AlertOctagon, ClipboardCheck, Clock, CheckSquare,
    Mail, Cpu, PlusCircle, RefreshCw, FileText, MessageSquare,
    LogIn, XCircle, ChevronRight
} from 'lucide-react';
import { baseURL } from '@/config/APIConfig';
import { useNavigate } from 'react-router-dom';

const API = baseURL.local; // '/api/' - Note: already has trailing slash
const dashboardApi = baseURL.dashboardApi; // 'https://pyapi.shipez.ai/api' - For seeding notifications

// Icon mapping
const ICON_MAP = {
    'file-up': FileUp,
    'sparkles': Sparkles,
    'alert-triangle': AlertTriangle,
    'file-check': FileCheck,
    'eye': Eye,
    'ship': Ship,
    'check-circle': CheckCircle,
    'alert-circle': AlertCircle,
    'link': Link,
    'package': Package,
    'map-pin': MapPin,
    'alert-octagon': AlertOctagon,
    'clipboard-check': ClipboardCheck,
    'clock': Clock,
    'check-square': CheckSquare,
    'mail': Mail,
    'cpu': Cpu,
    'plus-circle': PlusCircle,
    'refresh-cw': RefreshCw,
    'file-text': FileText,
    'message-square': MessageSquare,
    'log-in': LogIn,
    'settings': Settings,
    'x-circle': XCircle,
    'bell': Bell,
};

// Color mapping for notification types
const COLOR_CLASSES = {
    blue: 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400',
    purple: 'bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400',
    amber: 'bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400',
    green: 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400',
    orange: 'bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400',
    red: 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400',
    indigo: 'bg-indigo-100 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400',
    cyan: 'bg-cyan-100 text-cyan-600 dark:bg-cyan-900/30 dark:text-cyan-400',
    slate: 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400',
};

const PRIORITY_COLORS = {
    critical: 'border-l-4 border-red-500',
    high: 'border-l-4 border-amber-500',
    normal: '',
    low: 'opacity-75',
};

// Format relative time
const formatRelativeTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    return date.toLocaleDateString();
};

const NotificationItem = ({ notification, onMarkRead, onDelete, onNavigate }) => {
    const IconComponent = ICON_MAP[notification.icon] || Bell;
    const colorClass = COLOR_CLASSES[notification.color] || COLOR_CLASSES.blue;
    const priorityClass = PRIORITY_COLORS[notification.priority] || '';

    return (
        <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            className={`group p-3 sm:p-4 hover:bg-slate-50 dark:hover:bg-slate-800/50 cursor-pointer transition-all ${priorityClass} ${!notification.read ? 'bg-blue-50/50 dark:bg-blue-900/10' : ''}`}
            onClick={() => {
                if (!notification.read) onMarkRead(notification.id);
                if (notification.link) onNavigate(notification.link);
            }}
        >
            <div className="flex items-start gap-3">
                {/* Icon */}
                <div className={`flex-shrink-0 w-9 h-9 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl flex items-center justify-center ${colorClass}`}>
                    <IconComponent className="w-4 h-4 sm:w-5 sm:h-5" />
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                            <p className={`text-xs sm:text-sm font-semibold truncate ${!notification.read ? 'text-slate-900 dark:text-white' : 'text-slate-700 dark:text-slate-300'}`}>
                                {notification.title}
                            </p>
                            <p className="text-[10px] sm:text-xs text-slate-500 dark:text-slate-400 mt-0.5 line-clamp-2">
                                {notification.message}
                            </p>
                        </div>

                        {/* Unread indicator */}
                        {!notification.read && (
                            <span className="flex-shrink-0 w-2 h-2 bg-blue-500 rounded-full mt-1.5" />
                        )}
                    </div>

                    {/* Footer */}
                    <div className="flex items-center justify-between mt-2">
                        <span className="text-[10px] sm:text-xs text-slate-400 dark:text-slate-500">
                            {formatRelativeTime(notification.created_at)}
                        </span>

                        {/* Actions */}
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            {!notification.read && (
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onMarkRead(notification.id);
                                    }}
                                    className="p-1 hover:bg-slate-200 dark:hover:bg-slate-700 rounded text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                                    title="Mark as read"
                                >
                                    <Check className="w-3.5 h-3.5" />
                                </button>
                            )}
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onDelete(notification.id);
                                }}
                                className="p-1 hover:bg-red-100 dark:hover:bg-red-900/30 rounded text-slate-400 hover:text-red-500"
                                title="Delete"
                            >
                                <Trash2 className="w-3.5 h-3.5" />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </motion.div>
    );
};

const NotificationCenter = ({ isOpen, onClose, onUnreadCountChange }) => {
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [categoryCounts, setCategoryCounts] = useState({});
    const [activeCategory, setActiveCategory] = useState('all');
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const panelRef = useRef(null);

    const categories = [
        { id: 'all', label: 'All', icon: Bell },
        { id: 'documents', label: 'Documents', icon: FileText },
        { id: 'containers', label: 'Containers', icon: Package },
        { id: 'bookings', label: 'Bookings', icon: Ship },
        { id: 'system', label: 'System', icon: Settings },
    ];

    // Fetch notifications
    const fetchNotifications = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${dashboardApi}/notifications`, {
                params: {
                    category: activeCategory !== 'all' ? activeCategory : undefined,
                    limit: 50
                }
            });
            setNotifications(response.data.notifications || []);
            setUnreadCount(response.data.unread_count || 0);
            setCategoryCounts(response.data.category_counts || {});
            if (onUnreadCountChange) onUnreadCountChange(response.data.unread_count || 0);
        } catch (error) {
            console.error('Error fetching notifications:', error);
            // Generate sample data if API fails
            generateSampleNotifications();
        } finally {
            setLoading(false);
        }
    };

    // Generate sample notifications for demo
    const generateSampleNotifications = () => {
        const samples = [
            {
                id: '1',
                type: 'document_uploaded',
                category: 'documents',
                title: 'Document Uploaded',
                message: 'Bill of Lading BL-2025-001234 uploaded successfully',
                icon: 'file-up',
                color: 'blue',
                link: '/booking',
                priority: 'normal',
                read: false,
                created_at: new Date(Date.now() - 5 * 60000).toISOString()
            },
            {
                id: '2',
                type: 'container_event',
                category: 'containers',
                title: 'Container Gate In',
                message: 'Container MSCU1234567 has arrived at Shanghai Port',
                icon: 'package',
                color: 'indigo',
                link: '/containers',
                priority: 'normal',
                read: false,
                created_at: new Date(Date.now() - 15 * 60000).toISOString()
            },
            {
                id: '3',
                type: 'document_validation_warning',
                category: 'documents',
                title: 'Validation Warning',
                message: '2 critical issues found in Packing List PL-456',
                icon: 'alert-triangle',
                color: 'amber',
                link: '/booking',
                priority: 'high',
                read: false,
                created_at: new Date(Date.now() - 30 * 60000).toISOString()
            },
            {
                id: '4',
                type: 'booking_created',
                category: 'bookings',
                title: 'New Booking Created',
                message: 'Booking TBA0987 created for Info-X Software Technology',
                icon: 'plus-circle',
                color: 'green',
                link: '/booking',
                priority: 'normal',
                read: false,
                created_at: new Date(Date.now() - 60 * 60000).toISOString()
            },
            {
                id: '5',
                type: 'quote_expiring',
                category: 'bookings',
                title: 'Quote Expiring Soon',
                message: 'Rate Quote RQ02260024 expires in 24 hours',
                icon: 'clock',
                color: 'amber',
                link: '/rate-quote',
                priority: 'high',
                read: false,
                created_at: new Date(Date.now() - 2 * 3600000).toISOString()
            },
            {
                id: '6',
                type: 'container_status_alert',
                category: 'containers',
                title: 'Customs Hold',
                message: 'Container CMAU9876543 is held for customs inspection',
                icon: 'alert-octagon',
                color: 'red',
                link: '/containers',
                priority: 'critical',
                read: false,
                created_at: new Date(Date.now() - 3 * 3600000).toISOString()
            },
            {
                id: '7',
                type: 'email_received',
                category: 'system',
                title: 'New Email',
                message: '3 new emails received in AI Automation inbox',
                icon: 'mail',
                color: 'blue',
                link: '/mailbox',
                priority: 'normal',
                read: true,
                created_at: new Date(Date.now() - 5 * 3600000).toISOString()
            },
            {
                id: '8',
                type: 'booking_status_changed',
                category: 'bookings',
                title: 'Status Update',
                message: 'Booking SHZ-2025-0456 changed to In Transit',
                icon: 'refresh-cw',
                color: 'blue',
                link: '/booking',
                priority: 'normal',
                read: true,
                created_at: new Date(Date.now() - 24 * 3600000).toISOString()
            },
        ];

        const filteredSamples = activeCategory === 'all'
            ? samples
            : samples.filter(n => n.category === activeCategory);

        setNotifications(filteredSamples);
        setUnreadCount(samples.filter(n => !n.read).length);
        setCategoryCounts({
            all: samples.length,
            documents: samples.filter(n => n.category === 'documents').length,
            containers: samples.filter(n => n.category === 'containers').length,
            bookings: samples.filter(n => n.category === 'bookings').length,
            system: samples.filter(n => n.category === 'system').length,
        });
    };

    // Mark single notification as read
    const markAsRead = async (notificationId) => {
        try {
            await axios.patch(`${API}/notifications/${notificationId}/read`);
            setNotifications(prev =>
                prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
            );
            setUnreadCount(prev => Math.max(0, prev - 1));
            if (onUnreadCountChange) onUnreadCountChange(Math.max(0, unreadCount - 1));
        } catch (error) {
            console.error('Error marking notification as read:', error);
            // Update locally anyway
            setNotifications(prev =>
                prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
            );
        }
    };

    // Mark all as read
    const markAllAsRead = async () => {
        try {
            await axios.patch(`${API}/notifications/read-all`);
            setNotifications(prev => prev.map(n => ({ ...n, read: true })));
            setUnreadCount(0);
            if (onUnreadCountChange) onUnreadCountChange(0);
        } catch (error) {
            console.error('Error marking all as read:', error);
            setNotifications(prev => prev.map(n => ({ ...n, read: true })));
        }
    };

    // Delete notification
    const deleteNotification = async (notificationId) => {
        try {
            await axios.delete(`${API}/notifications/${notificationId}`);
            const deleted = notifications.find(n => n.id === notificationId);
            setNotifications(prev => prev.filter(n => n.id !== notificationId));
            if (deleted && !deleted.read) {
                setUnreadCount(prev => Math.max(0, prev - 1));
                if (onUnreadCountChange) onUnreadCountChange(Math.max(0, unreadCount - 1));
            }
        } catch (error) {
            console.error('Error deleting notification:', error);
            setNotifications(prev => prev.filter(n => n.id !== notificationId));
        }
    };

    // Navigate to link
    const handleNavigate = (link) => {
        onClose();
        navigate(link);
    };

    // Seed notifications
    const seedNotifications = async () => {
        try {
            await axios.post(`${API}/notifications/seed`);
            fetchNotifications();
        } catch (error) {
            console.error('Error seeding notifications:', error);
            generateSampleNotifications();
        }
    };

    useEffect(() => {
        if (isOpen) {
            fetchNotifications();
        }
    }, [isOpen, activeCategory]);

    // Close on click outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (panelRef.current && !panelRef.current.contains(event.target)) {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    return (
        <AnimatePresence>
            <motion.div
                ref={panelRef}
                initial={{ opacity: 0, y: -10, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -10, scale: 0.95 }}
                transition={{ duration: 0.2 }}
                className="absolute right-0 top-full mt-2 w-[calc(100vw-24px)] sm:w-[420px] max-h-[80vh] bg-white dark:bg-slate-900 rounded-xl sm:rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700 overflow-hidden z-[9999]"
                style={{ maxWidth: '420px' }}
            >
                {/* Header */}
                <div className="bg-gradient-to-r from-slate-800 to-slate-900 dark:from-slate-800 dark:to-slate-900 p-3 sm:p-4">
                    <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                            <Bell className="w-5 h-5 text-white" />
                            <h2 className="text-base sm:text-lg font-bold text-white">Notifications</h2>
                            {unreadCount > 0 && (
                                <span className="px-2 py-0.5 bg-red-500 text-white text-xs font-bold rounded-full">
                                    {unreadCount}
                                </span>
                            )}
                        </div>
                        <div className="flex items-center gap-1">
                            {unreadCount > 0 && (
                                <button
                                    onClick={markAllAsRead}
                                    className="p-1.5 sm:p-2 hover:bg-white/10 rounded-lg text-white/70 hover:text-white transition-colors"
                                    title="Mark all as read"
                                >
                                    <CheckCheck className="w-4 h-4 sm:w-5 sm:h-5" />
                                </button>
                            )}
                            <button
                                onClick={onClose}
                                className="p-1.5 sm:p-2 hover:bg-white/10 rounded-lg text-white/70 hover:text-white transition-colors"
                            >
                                <X className="w-4 h-4 sm:w-5 sm:h-5" />
                            </button>
                        </div>
                    </div>

                    {/* Category Tabs */}
                    <div className="flex items-center gap-1 overflow-x-auto scrollbar-hide pb-1">
                        {categories.map((cat) => {
                            const count = categoryCounts[cat.id] || 0;
                            return (
                                <button
                                    key={cat.id}
                                    onClick={() => setActiveCategory(cat.id)}
                                    className={`flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 rounded-lg text-xs sm:text-sm font-medium transition-all whitespace-nowrap ${activeCategory === cat.id
                                        ? 'bg-white text-slate-900'
                                        : 'text-white/70 hover:text-white hover:bg-white/10'
                                        }`}
                                >
                                    <cat.icon className="w-3.5 h-3.5" />
                                    <span className="hidden sm:inline">{cat.label}</span>
                                    <span className="sm:hidden">{cat.label.slice(0, 3)}</span>
                                    {count > 0 && (
                                        <span className={`ml-0.5 px-1.5 py-0.5 text-[10px] rounded-full ${activeCategory === cat.id
                                            ? 'bg-slate-200 text-slate-700'
                                            : 'bg-white/20 text-white'
                                            }`}>
                                            {count}
                                        </span>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Notifications List */}
                <div className="max-h-[calc(80vh-180px)] overflow-y-auto">
                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                        </div>
                    ) : notifications.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-12 px-4">
                            <div className="w-16 h-16 bg-slate-100 dark:bg-slate-800 rounded-full flex items-center justify-center mb-4">
                                <Bell className="w-8 h-8 text-slate-400" />
                            </div>
                            <p className="text-slate-500 dark:text-slate-400 text-sm text-center">
                                No notifications yet
                            </p>
                            <button
                                onClick={seedNotifications}
                                className="mt-4 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white text-xs rounded-lg transition-colors"
                            >
                                Generate Sample Notifications
                            </button>
                        </div>
                    ) : (
                        <div className="divide-y divide-slate-100 dark:divide-slate-800">
                            <AnimatePresence>
                                {notifications.map((notification) => (
                                    <NotificationItem
                                        key={notification.id}
                                        notification={notification}
                                        onMarkRead={markAsRead}
                                        onDelete={deleteNotification}
                                        onNavigate={handleNavigate}
                                    />
                                ))}
                            </AnimatePresence>
                        </div>
                    )}
                </div>

                {/* Footer */}
                {notifications.length > 0 && (
                    <div className="border-t border-slate-200 dark:border-slate-700 p-3 bg-slate-50 dark:bg-slate-800/50">
                        <button
                            onClick={() => {
                                onClose();
                                navigate('/notifications');
                            }}
                            className="w-full flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                        >
                            View All Notifications
                            <ChevronRight className="w-4 h-4" />
                        </button>
                    </div>
                )}
            </motion.div>
        </AnimatePresence>
    );
};

export default NotificationCenter;
